import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, LogOut, Loader2, AtSign, Save } from "lucide-react";

export default function AdminProfile() {
  const { user, refreshMe, logout } = useAuth();

  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busyPwd, setBusyPwd] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Email cannot be empty"); return; }
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", { email: email.trim().toLowerCase(), name });
      await refreshMe();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingProfile(false); }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New password and confirmation don't match"); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusyPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyPwd(false); }
  };

  return (
    <div data-testid="admin-profile">
      <span className="eyebrow">Account</span>
      <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
        <ShieldCheck size={26} className="text-gold" /> Profile &amp; Security
      </h1>
      <span className="divider-gold" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Identity card */}
        <div className="card-elev p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-navy/60 mb-1">Signed in as</div>
              <div className="text-xl text-navy font-medium">{user?.name || user?.email}</div>
              <div className="text-navy/70 text-sm">{user?.email}</div>
              <div className="mt-2 inline-flex items-center gap-2 text-xs bg-navy text-cream px-2 py-1 rounded">
                <ShieldCheck size={12} className="text-gold" /> role: {user?.role}
              </div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-navy/10">
            <button onClick={logout} className="btn-outline-navy text-xs" data-testid="profile-logout">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>

        {/* Update identity (email + name) */}
        <form onSubmit={saveProfile} className="card-elev p-6" data-testid="update-profile-form">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 mb-3">
            <AtSign size={13} className="text-gold" /> Update your details
          </div>
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Full name"
              className="field-box"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="up-name"
            />
            <input
              type="email"
              placeholder="Email"
              className="field-box"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="up-email"
            />
          </div>
          <button type="submit" disabled={savingProfile}
                  className="btn-gold w-full mt-6 disabled:opacity-50"
                  data-testid="up-submit">
            {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {savingProfile ? "Saving…" : "Save changes"}
          </button>
          <div className="text-xs text-navy/50 mt-3 leading-relaxed">
            Changing your email will re-issue your session so you can keep working seamlessly.
            The old email is recorded in the audit log.
          </div>
        </form>

        {/* Change password */}
        <form onSubmit={submitPwd} className="card-elev p-6 lg:col-span-2" data-testid="change-password-form">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 mb-3">
            <KeyRound size={13} className="text-gold" /> Change password
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="password" placeholder="Current password" className="field-box"
                   value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="cp-current" />
            <input type="password" placeholder="New password (8+ chars)" className="field-box"
                   value={next} onChange={(e) => setNext(e.target.value)} minLength={8} required data-testid="cp-new" />
            <input type="password" placeholder="Confirm new password" className="field-box"
                   value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={8} required data-testid="cp-confirm" />
          </div>
          <button type="submit" disabled={busyPwd}
                  className="btn-gold mt-6 disabled:opacity-50" data-testid="cp-submit">
            {busyPwd ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {busyPwd ? "Updating…" : "Update password"}
          </button>
          <div className="text-xs text-navy/50 mt-3 leading-relaxed">
            Any active password reset links you may have requested will be invalidated immediately.
          </div>
        </form>
      </div>
    </div>
  );
}
